<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'
import { toKpiApiParams } from '@/composables/dashboard-kpi'
import { textColor } from '@/lib/utils'

const props = defineProps({
  filters: { type: Object, default: () => ({}) },
})

const router = useRouter()
const planningStore = usePlanningStore()
const data = ref(null)

const fetchData = async () => {
  data.value = null
  const res = await planningApi.getBrokenEquipmentSummaryAsync(toKpiApiParams(props.filters))
  data.value = res.data
}

onMounted(fetchData)
watch(() => props.filters, fetchData, { deep: true })

const openEquipment = () => router.push({ name: 'equipments' })

const conditionColor = (v) => planningStore.getConditionColor(v)
const conditionLabel = (v) => planningStore.getConditionLabel(v)
</script>

<template>
  <div style="padding: 16px; display: flex; flex-direction: column;">
    <div v-if="data === null" style="display:flex; justify-content:center; align-items:center; height:80px;">
      <q-spinner size="md" color="primary" />
    </div>
    <template v-else>
      <div class="text-subtitle1 text-weight-bold q-mb-sm" style="line-height: 1.3;">
        Are there any broken equipment?
      </div>

      <div v-if="data.total === 0" class="text-caption text-positive q-mt-xs">
        No broken or damaged equipment this week.
      </div>

      <template v-else>
        <div v-for="item in data.items" :key="item.id" style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
          <q-avatar size="40px" color="grey-2">
            <q-img
              v-if="item.avatar_file_url || item.avatar_url"
              loading="lazy"
              fit="contain"
              :src="item.avatar_file_url || item.avatar_url"
            />
            <q-icon
              v-else
              name="mdi-camera-outline"
              color="grey-5"
              size="24px"
            />
          </q-avatar>
          <div style="flex: 1; min-width: 0;">
            <div class="text-body2 text-weight-medium ellipsis">{{ item.code }} — {{ item.name }}</div>
            <div style="display: flex; gap: 4px; flex-wrap: wrap; margin-top: 3px;">
              <q-badge
                v-if="item.warehouse_name"
                :label="item.warehouse_name"
                color="blue-grey"
                text-color="white"
              />
              <q-badge
                :label="conditionLabel(item.latest_condition)"
                :color="conditionColor(item.latest_condition)"
                :text-color="textColor(conditionColor(item.latest_condition))"
              />
            </div>
          </div>
        </div>

        <div v-if="data.has_more" class="text-caption text-grey-6" style="margin-top: 4px;">
          {{ data.items.length }} out of {{ data.total }} broken equipment
        </div>

        <div style="margin-top: 6px;">
          <q-btn
            flat
            dense
            no-caps
            color="primary"
            icon="mdi-wrench-outline"
            label="Tap to open equipment"
            @click="openEquipment"
          />
        </div>
      </template>

      <div class="text-caption text-grey-5" style="margin-top: 8px;">Based on latest recorded condition</div>
    </template>
  </div>
</template>
